import { Alert, Spinner } from "react-bootstrap";
import { QRCode } from "antd";
import RIcon from "../reservas/components/ReservasIcons";
import { BEAUTY_FALLBACK_IMAGE } from "../reservas/components/ReservaShared";
import { cartTotals, formatDuration, formatMoney, formatPillDate, payAtVenue } from "./beautyBooking";
import { claveDeLinea, nombreDeLinea } from "../../helpers/beautyLineas";
import "./beauty.css";
import "../reservas/reservas.css";

const BEAUTY_LAST_KEY = "beauty_reservation:last";

// Gap 4 workaround: the status endpoint hides qrToken for partial/none
// payments, so the token returned by POST /beauty/reservation (kept in
// sessionStorage) is used, only for the same reservation.
const readStoredQr = (reservationCode) => {
	try {
		const raw = sessionStorage.getItem(BEAUTY_LAST_KEY);
		const last = raw ? JSON.parse(raw) : null;
		if (
			last?.qrToken &&
			reservationCode &&
			String(last.reservationCode) === String(reservationCode)
		) {
			return last.qrToken;
		}
	} catch (_) {}
	return "";
};

const cancellationLine = (info) => {
	const allowed = info?.cancellationAllowed;
	if (allowed === undefined || allowed === null) return "";
	if (allowed === false || allowed === "false") {
		return "Este salón no permite cancelaciones.";
	}
	const hours = Number(info?.cancellationPolicyHours);
	return hours > 0
		? `Podés cancelar hasta ${hours} ${hours === 1 ? "hora" : "horas"} antes del turno.`
		: "Podés cancelar tu turno desde Mis reservas.";
};

const Cell = ({ icon, label, children }) => (
	<div className="rg">
		<div className="l">
			<RIcon name={icon} width={14} height={14} /> {label}
		</div>
		<div className="v">{children}</div>
	</div>
);

const BeautyConfirmation = ({
	statusInfo,
	loading,
	error,
	reservationCode,
	barId,
	onRetry,
	navigate,
}) => {
	const isConfirmed = statusInfo?.status === "confirmed";
	const services = Array.isArray(statusInfo?.services)
		? statusInfo.services
		: [];
	const deposit = cartTotals(services).deposit;
	const depositPaid = ["partial", "paid"].includes(statusInfo?.paymentstatus);
	const hasTotal =
		statusInfo?.totalPrice !== null &&
		statusInfo?.totalPrice !== undefined &&
		Number.isFinite(Number(statusInfo.totalPrice));
	const qrValue = isConfirmed
		? statusInfo?.qrToken || readStoredQr(reservationCode)
		: "";
	const cancelText = cancellationLine(statusInfo);

	return (
		<div className="reservas-root">
			<div className="wrap">
				<div className="success">
					<div className="tick">
						<div className="ring">
							<RIcon name="check" width={38} height={38} />
						</div>
					</div>
					<h1>{isConfirmed ? "¡Turno confirmado!" : "Turno en proceso"}</h1>
					<p className="ssub">
						{isConfirmed
							? "Tu turno quedó reservado. Te enviamos el comprobante por email."
							: "Estamos terminando de validar el estado de tu turno."}
					</p>

					{loading && (
						<div style={{ margin: "12px 0" }}>
							<Spinner animation="border" size="sm" />
						</div>
					)}

					{(reservationCode || statusInfo) && (
						<div className="receipt beauty-receipt">
							<div className="receipt-top">
								<img
									src={statusInfo?.barImage || BEAUTY_FALLBACK_IMAGE}
									alt="Salón"
									onError={(e) => {
										e.currentTarget.src = BEAUTY_FALLBACK_IMAGE;
									}}
								/>
								<div>
									<div className="rt-name">
										{statusInfo?.barName || "Tu salón"}
									</div>
									{statusInfo?.address && (
										<div className="rt-loc">
											<RIcon name="pin" width={14} height={14} />
											{statusInfo.address}
										</div>
									)}
								</div>
								{reservationCode && (
									<div className="rt-code">
										<div className="l">Código</div>
										<div className="c">{reservationCode}</div>
									</div>
								)}
							</div>
							<div className="receipt-grid">
								{statusInfo?.date && (
									<Cell icon="cal" label="Fecha">
										{formatPillDate(statusInfo.date)}
									</Cell>
								)}
								{statusInfo?.slotStart && (
									<Cell icon="clock" label="Horario">
										{statusInfo.slotEnd
											? `${statusInfo.slotStart} a ${statusInfo.slotEnd} hs`
											: `${statusInfo.slotStart} hs`}
									</Cell>
								)}
								{statusInfo?.professionalName && (
									<Cell icon="user" label="Profesional">
										{statusInfo.professionalName}
									</Cell>
								)}
								{deposit > 0 && (
									<Cell icon="dollar" label="Seña">
										{formatMoney(deposit)}
										{depositPaid ? " pagada" : ""}
									</Cell>
								)}
								{deposit > 0 && hasTotal && (
									<Cell icon="dollar" label="Saldo en el local">
										{formatMoney(payAtVenue(statusInfo.totalPrice, deposit))}
									</Cell>
								)}
							</div>
							{services.length > 0 && (
								<div className="reservation-details-box">
									<div className="rdb-title">Servicios</div>
									{services.map((s, i) => (
										<div className="rdb-row" key={claveDeLinea(s, i)}>
											<span>
												<RIcon name="spark" width={14} height={14} /> {nombreDeLinea(s)}
											</span>
											<strong>
												{[
													Number(s.durationMinutes) > 0 ? formatDuration(s.durationMinutes) : "",
													Number.isFinite(Number(s.price)) && s.price !== null && s.price !== undefined
														? formatMoney(s.price)
														: "",
												]
													.filter(Boolean)
													.join(" · ")}
											</strong>
										</div>
									))}
								</div>
							)}
							{qrValue && (
								<div className="beauty-qr beauty-receipt-qr">
									<QRCode value={qrValue} size={190} />
									<p className="beauty-confirm-note">Mostrá este QR al llegar</p>
								</div>
							)}
						</div>
					)}

					{isConfirmed && !qrValue && (
						<p className="beauty-confirm-note">
							Vas a encontrar el QR en el comprobante que te enviamos por
							email.
						</p>
					)}

					{statusInfo && !isConfirmed && (
						<p className="beauty-confirm-note">
							Estado: <strong>{statusInfo.status}</strong> · Pago:{" "}
							<strong>{statusInfo.paymentstatus}</strong>
						</p>
					)}
					{cancelText && <p className="beauty-confirm-note">{cancelText}</p>}
					{error && (
						<Alert variant="warning">
							{error}{" "}
							<button
								type="button"
								className="r-btn-soft ms-2"
								onClick={onRetry}
							>
								Reintentar
							</button>
						</Alert>
					)}

					<div className="success-actions">
						<button
							className="r-btn-soft"
							style={{ padding: "13px 24px" }}
							onClick={() => navigate("/beauty/historial")}
						>
							Ver mis reservas
						</button>
						<button
							className="r-btn-primary"
							style={{ padding: "13px 28px" }}
							onClick={() => navigate(barId ? `/beauty/${barId}` : "/reservas")}
						>
							Reservar otro turno
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BeautyConfirmation;
